import React, { useEffect, useState } from "react";
import { LineItemOrderDefinition, EstimateMatchCriteria, LineItemOrderObject } from "../../../types";
import { useOrganizationSelect } from "../../../../hooks/useOrganizationSelect";
import { useCarriers } from "../../../../hooks/useCarriers";
import { useTpas } from "../../../../hooks/useTpas";
import OrganizationSelector from "../../../../components/ui/utils/OrganizationSelector";
import { FilterableSelectGeneric } from "../../../components/GenericInputComponents";

interface CopyOrderFromModalProps {
	isOpen: boolean;
	onClose: () => void;
	currentMatchCriteria: EstimateMatchCriteria;
	lineItemOrderDefinitions: LineItemOrderDefinition[];
	onCopy: (sourceObjects: LineItemOrderObject[]) => void;
}

const CopyOrderFromModal: React.FC<CopyOrderFromModalProps> = ({
	isOpen,
	onClose,
	currentMatchCriteria,
	lineItemOrderDefinitions,
	onCopy,
}) => {
	const { carriers } = useCarriers();
	const { tpas } = useTpas();
	const { organizations, selectedOrganization, setSelectedOrganization } = useOrganizationSelect({ includeTestOrgs: true });

	const [sourceCriteria, setSourceCriteria] = useState<EstimateMatchCriteria>({
		estimateType: currentMatchCriteria.estimateType,
	});

	// Reset when modal opens
	useEffect(() => {
		if (isOpen) {
			setSourceCriteria({ estimateType: currentMatchCriteria.estimateType });
			setSelectedOrganization(undefined);
		}
	}, [isOpen, currentMatchCriteria.estimateType]);

	if (!isOpen) return null;

	const matchingDefinition = lineItemOrderDefinitions.find(
		(def) =>
			def.matchCriteria.estimateType === currentMatchCriteria.estimateType &&
			def.matchCriteria.organizationId === sourceCriteria.organizationId &&
			def.matchCriteria.carrierId === sourceCriteria.carrierId &&
			def.matchCriteria.tpaId === sourceCriteria.tpaId,
	);

	const isSameAsCurrent =
		sourceCriteria.organizationId === currentMatchCriteria.organizationId &&
		sourceCriteria.carrierId === currentMatchCriteria.carrierId &&
		sourceCriteria.tpaId === currentMatchCriteria.tpaId;

	const hasSelection = sourceCriteria.organizationId !== undefined || sourceCriteria.carrierId !== undefined || sourceCriteria.tpaId !== undefined;

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
			<div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4">
				<h3 className="text-lg font-semibold text-gray-900 mb-1">Copy Order From</h3>
				<p className="text-sm text-gray-500 mb-5">
					Select a Customer, TPA, and Carrier to copy their line item order into the current selection.
					Estimate type stays fixed as <span className="font-medium">{currentMatchCriteria.estimateType}</span>.
				</p>

				<div className="space-y-4">
					<div>
						<OrganizationSelector
							organizations={organizations}
							selectedOrganization={selectedOrganization}
							onSelect={(org) => {
								const organizationId = org?.organizationId === "default" ? undefined : org?.organizationId;
								setSelectedOrganization(org);
								setSourceCriteria((prev) => ({ ...prev, organizationId }));
							}}
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">TPA</label>
						<FilterableSelectGeneric
							options={tpas}
							value={tpas.find((tpa) => tpa.id === sourceCriteria.tpaId)}
							onSelect={(tpa) => setSourceCriteria((prev) => ({ ...prev, tpaId: tpa?.id }))}
							getDisplayValue={(tpa) => tpa.name}
							getKey={(tpa) => tpa.id}
							placeholder="Select TPA..."
						/>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-700 mb-2">Carrier</label>
						<FilterableSelectGeneric
							options={carriers}
							value={carriers.find((carrier) => carrier.id === sourceCriteria.carrierId)}
							onSelect={(carrier) => setSourceCriteria((prev) => ({ ...prev, carrierId: carrier?.id }))}
							getDisplayValue={(carrier) => carrier.name}
							getKey={(carrier) => carrier.id}
							placeholder="Select Carrier..."
						/>
					</div>
				</div>

				{hasSelection && isSameAsCurrent && (
					<p className="mt-4 text-sm text-amber-600">
						This is the same as the current selection. Please choose a different combination.
					</p>
				)}

				{hasSelection && !isSameAsCurrent && !matchingDefinition && (
					<p className="mt-4 text-sm text-amber-600">
						No line item order found for this combination.
					</p>
				)}

				{matchingDefinition && !isSameAsCurrent && (
					<p className="mt-4 text-sm text-green-600">
						Found {matchingDefinition.lineItemOrderObjects.length} line item{matchingDefinition.lineItemOrderObjects.length !== 1 ? "s" : ""} to copy.
					</p>
				)}

				<div className="flex justify-end gap-3 mt-6">
					<button
						onClick={onClose}
						className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
					>
						Cancel
					</button>
					<button
						onClick={() => {
							if (matchingDefinition && !isSameAsCurrent) {
								onCopy([...matchingDefinition.lineItemOrderObjects].sort((a, b) => a.index - b.index));
								onClose();
							}
						}}
						disabled={!matchingDefinition || isSameAsCurrent}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
					>
						Copy Order
					</button>
				</div>
			</div>
		</div>
	);
};

export default CopyOrderFromModal;
